import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Button } from '../components/ui/button';
import { ChartBar, ArrowsClockwise } from '@phosphor-icons/react';
import { useAgency } from '../context/AgencyContext';
import { useCrossAgencyDashboard } from './cross-agency-dashboard/useCrossAgencyDashboard';
import CrossAgencySummaryCards from './cross-agency-dashboard/CrossAgencySummaryCards';
import ClaimsStatusOverview from './cross-agency-dashboard/ClaimsStatusOverview';
import AgencyPerformanceTable from './cross-agency-dashboard/AgencyPerformanceTable';

const TIME_RANGES = [
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'quarter', label: 'This Quarter' },
  { value: 'year', label: 'This Year' },
  { value: 'all', label: 'All Time' },
];

const sumBy = (list, key) =>
  (list || []).reduce((acc, item) => acc + (item[key] || 0), 0);

const CrossAgencyDashboard = () => {
  const { isAzaiStaff, azaiRole } = useAgency();
  const [timeRange, setTimeRange] = useState('month');
  const { stats, loading, refresh } = useCrossAgencyDashboard({
    isAzaiStaff,
    timeRange,
  });

  const claimsRollup = useMemo(() => {
    if (!stats?.by_agency) return { paid: 0, denied: 0, pending: 0 };
    return {
      paid: sumBy(stats.by_agency, 'claims_paid'),
      denied: sumBy(stats.by_agency, 'claims_denied'),
      pending: sumBy(stats.by_agency, 'claims_pending'),
    };
  }, [stats]);

  if (!isAzaiStaff) return null;

  const canViewCrossAgency =
    azaiRole === 'super_admin' || azaiRole === 'billing_manager';
  if (!canViewCrossAgency) return null;

  if (loading) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="cross-agency-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <ChartBar size={24} className="text-indigo-600" />
            Cross-Agency Overview
          </h2>
          <p className="text-sm text-slate-500">
            Aggregate statistics across all agencies
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32" data-testid="ca-time-range">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_RANGES.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            data-testid="ca-refresh-btn"
          >
            <ArrowsClockwise size={16} />
          </Button>
        </div>
      </div>

      <CrossAgencySummaryCards stats={stats} />

      <ClaimsStatusOverview
        claimsPaid={claimsRollup.paid}
        claimsPending={claimsRollup.pending}
        claimsDenied={claimsRollup.denied}
        totalClaims={stats.total_claims || 0}
      />

      <AgencyPerformanceTable agencies={stats.by_agency} />
    </div>
  );
};

export default CrossAgencyDashboard;
